import { ChangeDetectionStrategy, Component, input, signal } from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { RouterLink } from '@angular/router';
import { PageService, RelatedPageInfo } from '@examdojo/category/v2';
import { IconComponent } from '@examdojo/core/icon';
import { TranslocoPipe } from '@jsverse/transloco';
import { of, switchMap, tap } from 'rxjs';
import { RootUrlParts } from '../../../app.model';

@Component({
  selector: 'dojo-related-articles',
  standalone: true,
  imports: [RouterLink, TranslocoPipe, IconComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    @if (relatedPages().length > 0) {
      <div class="mt-8 border-t border-neutral-200 pt-6">
        <h3 class="text-primary mb-4 text-lg font-semibold">
          {{ 'learn_hub.related_articles' | transloco }}
        </h3>
        <div class="flex flex-col gap-3">
          @for (page of relatedPages(); track page.page_slug) {
            <a
              [routerLink]="getRouterLink(page)"
              class="flex items-center gap-3 rounded-lg border border-neutral-200 px-4 py-3 transition-colors hover:bg-neutral-50"
            >
              <dojo-icon [icon]="'arrow-right'" [size]="'16'" class="text-neutral-400" />
              <span class="text-primary font-medium">{{ page.page_title }}</span>
            </a>
          }
        </div>
      </div>
    }
  `,
})
export class RelatedArticlesComponent {
  constructor(private readonly pageService: PageService) {
    this.fetchRelatedPages().pipe(takeUntilDestroyed()).subscribe();
  }

  readonly slugs = input<string[] | null>(null);
  readonly relatedPages = signal<RelatedPageInfo[]>([]);

  getRouterLink(page: RelatedPageInfo): string {
    return `/${RootUrlParts.LearnHub}/${page.topic_level_1_slug}/${page.topic_level_2_slug}/${page.page_slug}`;
  }

  private fetchRelatedPages() {
    return toObservable(this.slugs).pipe(
      switchMap((slugs) => {
        if (slugs && slugs.length > 0) {
          return this.pageService.fetchRelatedPageInfo(slugs);
        }
        return of([] as RelatedPageInfo[]);
      }),
      tap((pages) => this.relatedPages.set(pages)),
    );
  }
}
